import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import Header from '../components/Header'
import { safeNext, recalledReset, forgetReset, describeResetError } from '../lib/passwordReset'

// Read before Supabase tidies the address bar: an expired or already-used
// link comes back as /reset-password#error_code=otp_expired&error_description=…
const initialHash = typeof window !== 'undefined' ? new URLSearchParams(window.location.hash.slice(1)) : new URLSearchParams()

// Three ways in:
//  1. Email button  → /reset-password?token_hash=…&type=recovery  — nothing is
//     checked until they tap Continue, so email scanners can't use the link up
//  2. Older emails  → Supabase signs them in and lands here already signed in
//  3. Code          → /reset-password?code=1 (email + the code from the email)
export default function ResetPassword() {
const [searchParams] = useSearchParams()
const navigate = useNavigate()
const { user, loading: authLoading } = useAuth()
const saved = recalledReset()
const next = safeNext(searchParams.get('next')) || saved.next || '/'
const tokenHash = searchParams.get('token_hash')
const linkError = initialHash.get('error_code') || initialHash.get('error') || searchParams.get('error_code') || searchParams.get('error')

const [stage, setStage] = useState(linkError ? 'expired' : tokenHash ? 'continue' : searchParams.get('code') ? 'code' : 'checking')
const [codeEmail, setCodeEmail] = useState(searchParams.get('email') || saved.email || '')
const [code, setCode] = useState('')
const [password, setPassword] = useState('')
const [confirm, setConfirm] = useState('')
const [showPassword, setShowPassword] = useState(false)
const [showConfirm, setShowConfirm] = useState(false)
const [loading, setLoading] = useState(false)
const [error, setError] = useState('')

// Older-style link: once the saved session has loaded, either they're
// signed in (show the form) or the link didn't work
useEffect(() => {
if (stage !== 'checking' || authLoading) return
setStage(user ? 'form' : 'expired')
}, [stage, authLoading, user])

const verify = async (params) => {
setError(''); setLoading(true)
const { error: err } = await supabase.auth.verifyOtp({ ...params, type: 'recovery' })
setLoading(false)
if (err) { setError(describeResetError(err)); return }
window.history.replaceState(null, '', '/reset-password')
setStage('form')
}

const handleSubmit = async () => {
setError('')
if (password.length < 6) { setError('Password must be at least 6 characters'); return }
if (password !== confirm) { setError('Passwords do not match'); return }
setLoading(true)
const { error } = await supabase.auth.updateUser({ password })
setLoading(false)
if (error) { setError(describeResetError(error)); return }
forgetReset()
setStage('done')
// They're signed in now — take them where they were heading (e.g. Payouts)
setTimeout(() => navigate(next, { replace: true }), 1500)
}

const newLinkUrl = `/forgot-password?${new URLSearchParams({ ...(codeEmail ? { email: codeEmail } : {}), ...(next !== '/' ? { next } : {}) })}`
const heading = { form: 'Set a new password', done: 'Password updated', expired: 'This link has expired', code: 'Enter your code', continue: 'Reset your password', checking: 'Reset your password' }[stage]

return (
<div>
<Header />
<div style={{ display: 'flex', justifyContent: 'center', padding: '3rem 1rem' }}>
<div style={{ width: '100%', maxWidth: 420 }}>
<div style={{ textAlign: 'center', marginBottom: '2rem' }}>
<div style={{ fontSize: 40, marginBottom: 8 }}>{stage === 'expired' ? '⏰' : '🔑'}</div>
<h1 style={{ fontSize: 24, fontWeight: 600, color: 'var(--navy)' }}>{heading}</h1>
<p style={{ color: 'var(--text-muted)', marginTop: 4 }}>
{stage === 'form' && 'Choose something at least 6 characters long'}
{stage === 'done' && 'You\'re signed in — taking you back now'}
{stage === 'expired' && 'Reset links only work once and expire after 1 hour'}
{stage === 'code' && 'Type the code from your reset email'}
{stage === 'continue' && 'Tap continue to set a new password'}
</p>
</div>

<div className="card card-body">
{stage === 'checking' && <div className="spinner-wrap"><div className="spinner" /></div>}

{stage === 'done' && <div className="alert alert-success">Password updated! Taking you back…</div>}

{stage === 'expired' && (
<>
<div className="alert alert-error">This reset link has expired or has already been used. This can happen if you asked for more than one email, or the link is over an hour old.</div>
<Link to={newLinkUrl} className="btn btn-gradient btn-lg btn-full" style={{ textDecoration: 'none', textAlign: 'center', marginBottom: 8 }}>Send me a new link</Link>
<button className="btn btn-ghost btn-full" onClick={() => { setError(''); setStage('code') }}>I have a code from my email</button>
</>
)}

{stage === 'continue' && (
<>
{error && <div className="alert alert-error">{error}</div>}
<button className="btn btn-gradient btn-lg btn-full" onClick={() => verify({ token_hash: tokenHash })} disabled={loading}>
{loading ? '...' : 'Continue'}
</button>
{error && <Link to={newLinkUrl} className="btn btn-ghost btn-full" style={{ textDecoration: 'none', textAlign: 'center', marginTop: 8 }}>Send me a new link</Link>}
</>
)}

{stage === 'code' && (
<>
<div className="form-group">
<label className="form-label">Email address</label>
<input className="form-control" type="email" placeholder="you@email.com" value={codeEmail} onChange={e => setCodeEmail(e.target.value)} />
</div>
<div className="form-group">
<label className="form-label">Code from your email</label>
<input className="form-control" inputMode="numeric" autoComplete="one-time-code" placeholder="Enter code" maxLength={10} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} onKeyDown={e => e.key === 'Enter' && codeEmail && code && verify({ email: codeEmail.trim(), token: code })} style={{ letterSpacing: 4, fontSize: 18 }} />
</div>
{error && <div className="alert alert-error">{error}</div>}
<button className="btn btn-gradient btn-lg btn-full" onClick={() => verify({ email: codeEmail.trim(), token: code })} disabled={loading || !codeEmail.trim() || code.length < 6}>
{loading ? '...' : 'Continue'}
</button>
<p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)', marginTop: 16, marginBottom: 0 }}>
Code not working? <Link to={newLinkUrl} style={{ color: 'var(--primary)', fontWeight: 500, textDecoration: 'none' }}>Send me a new one</Link>
</p>
</>
)}

{stage === 'form' && (
<>
<div className="form-group">
<label className="form-label">New password</label>
<div style={{ position: 'relative' }}>
<input className="form-control" type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSubmit()} style={{ paddingRight: 44 }} />
<button type="button" onClick={() => setShowPassword(s => !s)} aria-label={showPassword ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
{showPassword ? (
<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
) : (
<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
)}
</button>
</div>
</div>
<div className="form-group">
<label className="form-label">Confirm password</label>
<div style={{ position: 'relative' }}>
<input className="form-control" type={showConfirm ? 'text' : 'password'} placeholder="••••••••" value={confirm} onChange={e => setConfirm(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSubmit()} style={{ paddingRight: 44 }} />
<button type="button" onClick={() => setShowConfirm(s => !s)} aria-label={showConfirm ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
{showConfirm ? (
<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
) : (
<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
)}
</button>
</div>
</div>

{error && <div className="alert alert-error">{error}</div>}
<button className="btn btn-gradient btn-lg btn-full" onClick={handleSubmit} disabled={loading}>
{loading ? '...' : 'Update password'}
</button>
</>
)}
</div>
</div>
</div>
</div>
)
}
